/**
 * Unified Tab System - Static/Dashboard Tabs Hook
 * @module lib/tabs/useStaticTabs
 */

import { useCurrentUser } from "@lib/hooks/useCurrentUser";
import { usePermissions } from "@lib/hooks/usePermissions";
import { useMatches } from "@tanstack/react-router";
import { useMemo } from "react";
import { DASHBOARD_TABS, sortTabs } from "./config";
import { getTabIcon } from "./icons";
import { normalizePath } from "./route-utils";
import type { Tab } from "./types";

/**
 * Item IDs hidden when the user's pointage mode is NONE (Feuille de Temps unavailable).
 */
const MODE_NONE_HIDDEN_TAB_IDS = new Set(["pointage"]);

/**
 * Hook for static/dashboard tabs
 * Uses config-driven approach with icon resolution
 */
export function useDashboardTabs() {
  const { can } = usePermissions();
  const { user } = useCurrentUser();
  const modePointage = user?.modePointage;

  // useMatches gives us the current route matches for active tab detection
  const matches = useMatches();

  const tabs = useMemo((): Tab[] => {
    return DASHBOARD_TABS.filter((config) => {
      // Check permission if defined
      if (config.permission && !can(config.permission)) {
        return false;
      }
      // Hide mode-dependent features (pointage tab when mode is NONE)
      if (config.id && modePointage === "NONE" && MODE_NONE_HIDDEN_TAB_IDS.has(config.id)) {
        return false;
      }
      return true;
    }).map((config) => ({
      id: config.id,
      title: config.title,
      path: normalizePath(config.path),
      icon: getTabIcon(config.iconName),
      badge: undefined,
      order: config.order ?? 999,
      isActive: false, // Set below
      isDisabled: false,
    }));
  }, [can, modePointage]);

  const sortedTabs = useMemo(() => sortTabs(tabs), [tabs]);

  // Determine active tab from current route matches
  const activeTab = useMemo((): string | null => {
    if (matches.length === 0) return null;

    const currentPath = matches[matches.length - 1]?.fullPath ?? "";
    const normalizedCurrent = normalizePath(currentPath);

    const match = sortedTabs.find((tab) => {
      const normalizedPath = normalizePath(tab.path);
      // Exact match or prefix match
      return (
        normalizedPath === normalizedCurrent || normalizedCurrent.startsWith(`${normalizedPath}/`)
      );
    });

    return match?.path ?? null;
  }, [sortedTabs, matches]);

  // Set isActive on the matching tab
  const tabsWithActive = useMemo((): Tab[] => {
    return sortedTabs.map((tab) => ({
      ...tab,
      isActive: tab.path === activeTab,
    }));
  }, [sortedTabs, activeTab]);

  return {
    tabs: tabsWithActive,
    activeTab,
    isLoading: false,
    error: null,
  };
}
